"use client";

import { useState } from "react";
import { ChevronRight, Plus, RotateCcw, Trash2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    ScenarioBlock as ScenarioBlockType,
    ScenarioChoice,
    ScenarioQuality,
    ScenarioScene,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";
import {
    createScenarioChoice,
    createScenarioScene,
} from "../../../lib/blockRegistry";
import { resolveNextSceneId } from "../../../lib/scenario";
import RichText from "../text/richText";
import RichHtml from "./richHtml";

const QUALITIES: ScenarioQuality[] = ["good", "average", "poor"];

const QUALITY_BOX: Record<ScenarioQuality, string> = {
    good: "border-[hsl(var(--lomda-success))]/40 bg-[hsl(var(--lomda-success))]/10",
    average: "border-[hsl(var(--lomda-muted-text))]/40 bg-[hsl(var(--lomda-muted-text))]/10",
    poor: "border-[hsl(var(--lomda-destructive))]/40 bg-[hsl(var(--lomda-destructive))]/10",
};

const QUALITY_DOT: Record<ScenarioQuality, string> = {
    good: "bg-[hsl(var(--lomda-success))]",
    average: "bg-[hsl(var(--lomda-muted-text))]",
    poor: "bg-[hsl(var(--lomda-destructive))]",
};

function PreviewScenario({ scenes }: { scenes: ScenarioScene[] }) {
    const locale = useLomdaLocale();
    const [currentId, setCurrentId] = useState(scenes[0].id);
    const [picked, setPicked] = useState<string | null>(null);
    const [finished, setFinished] = useState(false);

    const scene = scenes.find((s) => s.id === currentId) ?? scenes[0];
    const chosen = scene.choices.find((c) => c.id === picked) ?? null;

    const restart = () => {
        setCurrentId(scenes[0].id);
        setPicked(null);
        setFinished(false);
    };

    const next = () => {
        if (!chosen) return;
        const target = resolveNextSceneId(scenes, scene.id, chosen.nextSceneId);
        setPicked(null);
        if (!target) setFinished(true);
        else setCurrentId(target);
    };

    if (finished) {
        return (
            <div className="lomda-v3-card-surface flex flex-col items-center gap-4 rounded-2xl border p-10 text-center shadow-sm">
                <h3 className="lomda-v3-text-h2">
                    {t("lomda-v3-editor.scenario.done", locale)}
                </h3>
                <button
                    type="button"
                    onClick={restart}
                    className="lomda-v3-text-small flex items-center gap-1.5 rounded-full border px-5 py-2.5 text-muted-foreground transition hover:text-foreground"
                >
                    <RotateCcw className="size-4" />
                    {t("lomda-v3-editor.scenario.restart", locale)}
                </button>
            </div>
        );
    }

    return (
        <div className="lomda-v3-card-surface flex flex-col gap-4 rounded-2xl border p-6 shadow-sm">
            <div className="lomda-v3-text-p">
                <RichHtml html={scene.dialogue} />
            </div>

            <div className="flex flex-col gap-2">
                {scene.choices
                    .filter((c) => c.text)
                    .map((c) => {
                        const isPicked = picked === c.id;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                disabled={picked !== null}
                                onClick={() => setPicked(c.id)}
                                className={cn(
                                    "lomda-v3-text-p rounded-xl border p-3 text-start transition disabled:cursor-default",
                                    picked === null &&
                                        "hover:border-primary hover:bg-primary/5",
                                    isPicked && QUALITY_BOX[c.quality]
                                )}
                            >
                                {c.text}
                            </button>
                        );
                    })}
            </div>

            {chosen && chosen.feedback && (
                <div
                    className={cn(
                        "lomda-v3-text-p rounded-xl border p-3",
                        QUALITY_BOX[chosen.quality]
                    )}
                >
                    {chosen.feedback}
                </div>
            )}

            {picked !== null && (
                <button
                    type="button"
                    onClick={next}
                    className="flex items-center gap-1.5 self-end rounded-full bg-primary px-5 py-2 lomda-v3-text-label text-primary-foreground transition hover:opacity-90"
                >
                    {t("lomda-v3-editor.scenario.continue", locale)}
                    <ChevronRight className="size-4 rtl:rotate-180" />
                </button>
            )}
        </div>
    );
}

export default function ScenarioBlock({
    block,
}: {
    block: ScenarioBlockType;
}) {
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);

    const editable = useBlockEditable();
    const scenes = block.data.scenes ?? [];

    const setScenes = (next: ScenarioScene[]) =>
        replaceBlockData(block.id, { scenes: next });
    const patchScene = (id: string, patch: Partial<ScenarioScene>) =>
        setScenes(scenes.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    const patchChoice = (
        sceneId: string,
        choiceId: string,
        patch: Partial<ScenarioChoice>
    ) =>
        setScenes(
            scenes.map((s) =>
                s.id === sceneId
                    ? {
                          ...s,
                          choices: s.choices.map((c) =>
                              c.id === choiceId ? { ...c, ...patch } : c
                          ),
                      }
                    : s
            )
        );

    if (!editable) {
        const valid = scenes.filter(
            (s) => s.dialogue && s.choices.some((c) => c.text)
        );
        if (valid.length === 0) return null;
        return <PreviewScenario scenes={valid} />;
    }

    return (
        <div className="flex flex-col gap-4">
            {scenes.map((scene, i) => (
                <div
                    key={scene.id}
                    className="lomda-v3-card-surface flex flex-col gap-3 rounded-2xl border p-4"
                >
                    <div className="flex items-center gap-2">
                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 lomda-v3-text-label text-muted-foreground">
                            {t("lomda-v3-editor.scenario.scene-n", undefined, {
                                number: i + 1,
                            })}
                        </span>
                        <Input
                            value={scene.name}
                            onChange={(e) =>
                                patchScene(scene.id, { name: e.target.value })
                            }
                            placeholder={t("lomda-v3-editor.scenario.scene-name")}
                            className="lomda-v3-page-text h-8 flex-1"
                        />
                        {scenes.length > 1 && (
                            <button
                                type="button"
                                aria-label={t("lomda-v3-editor.block.delete")}
                                onClick={() =>
                                    setScenes(
                                        scenes.filter((x) => x.id !== scene.id)
                                    )
                                }
                                className="flex size-7 items-center justify-center rounded text-muted-foreground transition hover:bg-muted hover:text-destructive"
                            >
                                <Trash2 className="size-4" />
                            </button>
                        )}
                    </div>

                    <div className="rounded-xl border p-3">
                        <RichText
                            initialHtml={scene.dialogue}
                            editable
                            useGlobalToolbar
                            placeholder={t("lomda-v3-editor.scenario.dialogue-placeholder")}
                            onCommit={(dialogue) =>
                                patchScene(scene.id, { dialogue })
                            }
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        {scene.choices.map((c) => (
                            <div
                                key={c.id}
                                className="flex flex-col gap-1.5 rounded-xl border p-2.5"
                            >
                                <div className="flex items-center gap-2">
                                    <div className="flex shrink-0 items-center gap-0.5 rounded-lg border p-0.5">
                                        {QUALITIES.map((q) => (
                                            <button
                                                key={q}
                                                type="button"
                                                title={t(
                                                    `lomda-v3-editor.scenario.quality-${q}`
                                                )}
                                                onClick={() =>
                                                    patchChoice(scene.id, c.id, {
                                                        quality: q,
                                                    })
                                                }
                                                className={cn(
                                                    "flex size-5 items-center justify-center rounded-full transition",
                                                    c.quality === q
                                                        ? "ring-2 ring-offset-1"
                                                        : "opacity-40"
                                                )}
                                            >
                                                <span
                                                    className={cn(
                                                        "size-3 rounded-full",
                                                        QUALITY_DOT[q]
                                                    )}
                                                />
                                            </button>
                                        ))}
                                    </div>
                                    <Input
                                        value={c.text}
                                        onChange={(e) =>
                                            patchChoice(scene.id, c.id, {
                                                text: e.target.value,
                                            })
                                        }
                                        placeholder={t("lomda-v3-editor.scenario.choice-placeholder")}
                                        className="lomda-v3-page-text h-8 flex-1"
                                    />
                                    {scene.choices.length > 1 && (
                                        <button
                                            type="button"
                                            aria-label={t("lomda-v3-editor.block.delete")}
                                            onClick={() =>
                                                patchScene(scene.id, {
                                                    choices: scene.choices.filter(
                                                        (x) => x.id !== c.id
                                                    ),
                                                })
                                            }
                                            className="flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground transition hover:bg-muted hover:text-destructive"
                                        >
                                            <Trash2 className="size-4" />
                                        </button>
                                    )}
                                </div>
                                <div className="flex flex-wrap items-center gap-2 ps-1">
                                    <Input
                                        value={c.feedback}
                                        onChange={(e) =>
                                            patchChoice(scene.id, c.id, {
                                                feedback: e.target.value,
                                            })
                                        }
                                        placeholder={t("lomda-v3-editor.scenario.feedback-placeholder")}
                                        className="h-7 flex-1 lomda-v3-text-small text-muted-foreground"
                                    />
                                    <div className="flex items-center gap-1.5">
                                        <span className="lomda-v3-text-small text-muted-foreground">
                                            {t("lomda-v3-editor.scenario.goto")}
                                        </span>
                                        <select
                                            value={c.nextSceneId}
                                            onChange={(e) =>
                                                patchChoice(scene.id, c.id, {
                                                    nextSceneId: e.target.value,
                                                })
                                            }
                                            className="h-7 rounded-lg border bg-card px-2 lomda-v3-text-small outline-none"
                                        >
                                            <option value="">
                                                {t("lomda-v3-editor.scenario.goto-next")}
                                            </option>
                                            {scenes.map((s, si) => (
                                                <option key={s.id} value={s.id}>
                                                    {s.name ||
                                                        t(
                                                            "lomda-v3-editor.scenario.scene-n",
                                                            undefined,
                                                            { number: si + 1 }
                                                        )}
                                                </option>
                                            ))}
                                            <option value="end">
                                                {t("lomda-v3-editor.scenario.goto-end")}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() =>
                                patchScene(scene.id, {
                                    choices: [
                                        ...scene.choices,
                                        createScenarioChoice(),
                                    ],
                                })
                            }
                            className="flex items-center gap-1.5 self-start rounded-lg px-2 py-1 lomda-v3-text-small text-muted-foreground transition hover:text-primary"
                        >
                            <Plus className="size-4" />
                            {t("lomda-v3-editor.scenario.add-choice")}
                        </button>
                    </div>
                </div>
            ))}
            <button
                type="button"
                onClick={() => setScenes([...scenes, createScenarioScene()])}
                className="flex items-center justify-center gap-1.5 rounded-xl border border-dashed py-2.5 lomda-v3-text-small text-muted-foreground transition hover:border-primary hover:text-primary"
            >
                <Plus className="size-4" />
                {t("lomda-v3-editor.scenario.add-scene")}
            </button>
        </div>
    );
}
